import React, { useEffect, useRef, useState } from "react";

const MAX = 4;
const dist = (a, b) => Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);

// Pinch / double-tap zoom that stays inside the image, never zooms the whole page.
export const ZoomableImage = ({ src, onZoomChange, className }) => {
  const [t, setT] = useState({ s: 1, x: 0, y: 0 });
  const [live, setLive] = useState(false);
  const g = useRef({});
  const lastTap = useRef(0);
  const ref = useRef(null);
  const zoomed = t.s > 1.01;

  useEffect(() => { if (onZoomChange) onZoomChange(zoomed); /* eslint-disable-next-line */ }, [zoomed]);

  const bound = (s, x, y) => {
    const el = ref.current;
    if (!el || s <= 1) return { s: 1, x: 0, y: 0 };
    const mx = (el.offsetWidth * (s - 1)) / 2, my = (el.offsetHeight * (s - 1)) / 2;
    return { s, x: Math.max(-mx, Math.min(mx, x)), y: Math.max(-my, Math.min(my, y)) };
  };

  const start = (e) => {
    const ts = e.touches;
    if (ts.length >= 2) { g.current = { mode: "pinch", multi: true, d0: dist(ts[0], ts[1]), s0: t.s, x0: t.x, y0: t.y }; setLive(true); }
    else if (zoomed) { g.current = { mode: "pan", multi: g.current.multi, px: ts[0].clientX, py: ts[0].clientY, x0: t.x, y0: t.y, moved: false }; setLive(true); }
    else g.current = { mode: null, multi: false, moved: false, px: ts[0].clientX, py: ts[0].clientY };
    if (g.current.multi || zoomed) e.stopPropagation();
  };

  const move = (e) => {
    const ts = e.touches, c = g.current;
    if (c.mode === "pinch" && ts.length >= 2) {
      const s = Math.min(MAX, Math.max(1, c.s0 * (dist(ts[0], ts[1]) / c.d0)));
      setT(bound(s, c.x0, c.y0));
    } else if (c.mode === "pan" && ts.length === 1) {
      const dx = ts[0].clientX - c.px, dy = ts[0].clientY - c.py;
      if (Math.abs(dx) + Math.abs(dy) > 6) c.moved = true;
      setT((p) => bound(p.s, c.x0 + dx, c.y0 + dy));
    } else if (ts.length === 1 && Math.abs(ts[0].clientX - (c.px || 0)) > 6) c.moved = true;
    if (c.multi || zoomed) e.stopPropagation();
  };

  const end = (e) => {
    const c = g.current;
    const stop = c.multi || zoomed;
    if (e.touches.length === 0) {
      setLive(false);
      if (!c.multi && !c.moved) {
        const now = Date.now();
        if (now - lastTap.current < 300) { setT(zoomed ? { s: 1, x: 0, y: 0 } : bound(2.5, 0, 0)); lastTap.current = 0; e.stopPropagation(); return; }
        lastTap.current = now;
      }
      setT((p) => (p.s < 1.05 ? { s: 1, x: 0, y: 0 } : p));
      g.current = {};
    } else if (e.touches.length === 1 && c.mode === "pinch") {
      g.current = { mode: "pan", multi: true, px: e.touches[0].clientX, py: e.touches[0].clientY, x0: t.x, y0: t.y, moved: true };
    }
    if (stop) e.stopPropagation();
  };

  return (
    <img
      ref={ref} src={src} alt="" draggable={false}
      onTouchStart={start} onTouchMove={move} onTouchEnd={end}
      onDoubleClick={(e) => { e.stopPropagation(); setT(zoomed ? { s: 1, x: 0, y: 0 } : bound(2.5, 0, 0)); }}
      onClick={(e) => e.stopPropagation()}
      className={`${className || ""} select-none ${zoomed ? "cursor-zoom-out" : "cursor-zoom-in"}`}
      style={{ transform: `translate3d(${t.x}px, ${t.y}px, 0) scale(${t.s})`, transition: live ? "none" : "transform 200ms ease-out", touchAction: "none", willChange: "transform" }}
      data-testid="lightbox-media"
    />
  );
};
